import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

function trimTrailingSlash(value: string) {
    return value.replace(/\/+$/, '');
}

function hasVersionedApiPath(value: string) {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getErrorMessage(payload: unknown, fallback: string) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const message = candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof message === 'string' && message.trim()) return message;
    }
    return fallback;
}

function unwrap<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) return payload as T;
    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) throw new Error(getErrorMessage(envelope, 'Patroni request failed'));
    return envelope.data !== undefined ? envelope.data : payload as T;
}

async function requestPatroni(path: string, init: RequestInit = {}): Promise<unknown> {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(String(
        import.meta.env.VITE_API_BASE_URL
        ?? import.meta.env.VITE_API_URL
        ?? settings.adminApiBaseUrl
        ?? 'https://test-api.pteultra.com/api/v1',
    ));
    const version = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${version}`;
    const token = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body) headers.set('Content-Type', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const response = await fetch(`${apiRoot}/AdminPatroni/${path}`, { ...init, headers });
    const contentType = response.headers.get('content-type') ?? '';
    const payload: unknown = contentType.includes('application/json')
        ? await response.json()
        : await response.text();

    if (!response.ok) throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    return unwrap(payload);
}

export function getPatroniCluster() {
    return requestPatroni('cluster', { method: 'GET' });
}

export function setPatroniLeader(member: string) {
    return requestPatroni('set-leader', {
        method: 'POST',
        body: JSON.stringify({ member }),
    });
}
